
import { Component, Vue } from "vue-property-decorator";
import * as Model from "@gigalot/data-models";
import { EventBus } from "../eventBus";
const Subscriber = require("@jetblack/graphql-reconnect-client");
import { createClient } from "@/helpers/graphql-ws-rtc-adapter";
import { DimssaButton } from "@/components/shared/dimssa-button.vue";

@Component({
  components: {
    DimssaButton,
  },
})
export default class AutoResults extends Vue {
  name = "autoResults";
  autoResults: any[] = [];
  rationPlans: Model.RationPlan[] = [];
  rationColorMap: Record<string, string> = {};
  loading = false;
  search = "";
  tableKey = 1;

  headers = [
    {
      text: "Scheduled Time",
      value: "MixStartTime",
      align: "start",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Ration",
      value: "RationCode",
      align: "start",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Planned (kg)",
      value: "RationWeightRequired",
      align: "end",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Delivered (kg)",
      value: "ActualWeight",
      align: "end",
      sortable: true,
      filterable: true,
      divider: true,
    },
    {
      text: "Difference (kg)",
      value: "difference",
      align: "end",
      sortable: true,
      filterable: false,
      divider: true,
    },
    {
      text: "Status",
      value: "status",
      align: "center",
      sortable: true,
      filterable: true,
      divider: true,
    },
  ];

  urlws = "wss://pi.gigalot.systems:7778/api/ws";
  options = {};
  graphQLSubscriber: any = undefined;
  graphQLUnsubscribe: any = undefined;
  subscriptions = `subscription($guid:String!){ autoResults(guid:$guid) }`;
  variables = { guid: this.$store.state.user.location.guid };
  operationName = null;

  mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.getData);
    this.$store.dispatch("user/addFirebaseCallback", this.getDataSocket);
  }

  destroyed() {
    if (this.graphQLUnsubscribe) {
      this.graphQLUnsubscribe();
    }
    if (this.graphQLSubscriber) {
      this.graphQLSubscriber();
    }
  }

  get moment() {
    return this.$store.state.moment;
  }

  get themeClass(): string {
    return this.$store.state.lightDarkMode;
  }

  get tableItems() {
    return (this.autoResults || []).map((result: any) => {
      const planned = Number(result?.RationWeightRequired) || 0;
      const actual = Number(result?.ActualWeight) || 0;
      const feedingResults = (result?.feedingResults || []).map((delivery: any) => {
        const plannedDelivery = Number(delivery?.jobAmount) || 0;
        const actualDelivery = Number(delivery?.amountFed) || 0;
        return {
          ...delivery,
          jobAmount: plannedDelivery,
          amountFed: actualDelivery,
          difference: actualDelivery - plannedDelivery,
        };
      });
      return {
        ...result,
        RationWeightRequired: planned,
        ActualWeight: actual,
        difference: actual - planned,
        feedingResults,
        rationColor: this.getRationColor(result?.RationCode),
      };
    });
  }

  get totalMixes() {
    return this.tableItems.length;
  }

  get totalDeliveries() {
    return this.tableItems.reduce((sum: number, item: any) => {
      return sum + (item?.feedingResults?.length || 0);
    }, 0);
  }

  get totalActualWeight() {
    return this.tableItems.reduce((sum: number, item: any) => {
      return sum + (Number(item?.ActualWeight) || 0);
    }, 0);
  }

  async refresh() {
    if (this.loading) return;
    await this.getData();
  }

  forceRerender() {
    this.tableKey += 1;
  }

  numberWithCommas(value: number | string | null | undefined) {
    if (value === null || value === undefined || value === "") return "0";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0";
    return Math.round(numeric)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  formatNumber(value: number | string | null | undefined) {
    return this.numberWithCommas(value);
  }

  formatDifference(value: number | string | null | undefined) {
    const numeric = Number(value) || 0;
    if (numeric === 0) return "0";
    const prefix = numeric > 0 ? "+" : "-";
    return `${prefix}${this.formatNumber(Math.abs(numeric))}`;
  }

  differenceClass(value: number | string | null | undefined) {
    const numeric = Number(value) || 0;
    if (numeric > 0) return "difference--positive";
    if (numeric < 0) return "difference--negative";
    return "difference--neutral";
  }

  formatTimestamp(value: number | string | null | undefined) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return "--:--";
    return this.moment(numeric).format("HH:mm");
  }

  statusColor(status: string) {
    if (!status) return "grey darken-1";
    const normalized = status.toString().toLowerCase();
    if (["complete", "completed", "done"].includes(normalized)) return "green";
    if (["mixing", "inprogress", "delivering", "progress"].includes(normalized)) return "blue";
    if (["pending", "scheduled", "queued"].includes(normalized)) return "orange darken-1";
    if (["error", "failed", "aborted"].includes(normalized)) return "red darken-1";
    return "grey darken-1";
  }

  normalizeColor(color?: string) {
    if (!color) return "grey darken-1";
    if (typeof color === "string") return color.toLowerCase();
    return `${color}`.toLowerCase();
  }

  buildRationColorMap() {
    const map: Record<string, string> = {};
    this.rationPlans.forEach((plan: Model.RationPlan) => {
      plan?.rationItems?.forEach((item: any) => {
        if (item?.ration && item?.colour) {
          map[item.ration] = item.colour;
        }
      });
    });
    this.rationColorMap = map;
  }

  getRationColor(ration: string) {
    return this.rationColorMap[ration] || "";
  }

  parseAutoResults(data: any) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (err) {
        console.warn("Unable to parse auto results payload", err);
        return [];
      }
    }
    return [data];
  }

  applyAutoResults(data: any) {
    this.autoResults = this.parseAutoResults(data);
    this.forceRerender();
  }

  async getData() {
    this.loading = true;
    try {
      const gql = `query($guid:String!){
        AutoResults(guid:$guid)
        RationPlans(guid:$guid) {
          guid
          rationItems {
            ration
            colour
          }
        }
      }`;
      const json = await this.$store.dispatch("graphQl", {
        gql,
        variables: { guid: this.$store.state.user.location.guid },
      });
      this.applyAutoResults(json?.data?.AutoResults);
      this.rationPlans = json?.data?.RationPlans || [];
      this.buildRationColorMap();
    } catch (err) {
      console.log("getautoResults -> error: " + err);
    } finally {
      this.loading = false;
    }
    EventBus.$emit("component-a-done");
  }

  async getDataSocket() {
    if (this.$store.state.useP2PConn) {
      await this.getOnlineFeedDeliveriesSocket();
    } else {
      await this.getOnsiteFeedDeliveriesSocket();
    }
  }

  async getOnlineFeedDeliveriesSocket() {
    const variables = {
      guid: this.$store.state.user.location.guid,
      user: this.$store.state.user.user.uid,
    };

    const subscribe = (subscriptions: string, variables: any, callback: (error: any, data: any) => Promise<void>) => {
      const client = createClient();
      let unsubscribe = client?.subscribe(
        {
          query: subscriptions,
          variables: variables,
        },
        {
          next: (message: any) => callback(undefined, message.data),
          error: (err: any) => {
            throw err;
          },
          complete: () => console.log("subscription completed"),
        }
      );
      return unsubscribe;
    };

    this.graphQLUnsubscribe = subscribe(this.subscriptions, variables, async (error: any, data: any) => {
      this.$store.state.wsConnected = true;
      if (!(error || subscribe)) {
        console.log("subscribe success");
        this.$store.state.wsDialogVisible = false;
        return;
      }
      if (error) {
        console.log("error subscribing");
        this.$store.state.wsConnected = false;
        if (error.event.type === "close") {
        } else {
          this.$store.state.wsDialogVisible = true;
          console.error(error);
        }
      }
      this.$store.state.wsDialogVisible = false;
      this.$store.state.wsConnected = true;
      if (data?.autoResults) {
        this.applyAutoResults(data.autoResults);
      }
    });
  }

  async getOnsiteFeedDeliveriesSocket() {
    this.graphQLSubscriber = Subscriber.graphQLReconnectingSubscriber(
      this.urlws,
      this.options,
      (error: any, subscribe: any) => {
        try {
          if (!(error || subscribe)) {
            console.log("connected to socket");
            this.$store.state.wsDialogVisible = false;
            return;
          }
          if (error) {
            console.log("error connecting to socket");
            if (error.event.type === "close") {
            } else {
              this.$store.state.wsDialogVisible = true;
              console.error(error);
            }
          }
          console.log("socket connected");
          this.graphQLUnsubscribe = subscribe(this.subscriptions, this.variables, this.operationName, (error: any, data: any) => {
            if (!(error || subscribe)) {
              console.log("subscribe success");
              this.$store.state.wsDialogVisible = false;
              return;
            }
            if (error) {
              console.log("error subscribing");
              if (error.event.type === "close") {
              } else {
                this.$store.state.wsDialogVisible = true;
                console.error(error);
              }
            }
            this.$store.state.wsDialogVisible = false;
            if (data?.autoResults) {
              this.applyAutoResults(data.autoResults);
            }
            this.loading = false;
          });
        } catch (err) {}
      },
      15000,
      3,
      "graphql-ws"
    );
  }
}
